/**
 * @fileoverview Canvas form block renderer for template authoring.
 * @module template-builder
 *
 * Responsibilities:
 * - Render template forms, sections, and fields on the canvas.
 * - Expose field-level actions such as move, duplicate, and delete.
 *
 * Dependencies:
 * - React state and callback hooks
 * - @dnd-kit drag-and-drop primitives
 * - Template domain contracts from templateTypes
 *
 * Architecture:
 * - UI-only component; persistence is callback-driven by the parent local-first service boundary.
 * - Does not call APIs, Dexie, or sync services directly.
 *
 * Notes:
 * - Field actions mutate parent-owned form state through callbacks only.
 */
import { useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { HeaderRichEditor } from './HeaderRichEditor';
import { ContactsConfigurator, ViolationsConfigurator } from './ModulePickerModal';
import {
  ChevronDown, ChevronRight, GripVertical, Pencil,
  Plus, Trash2, LayoutDashboard, AlertCircle, Check, Copy,
  Lock, FileText, MoreVertical, BookMarked, Link2, X, Sparkles, Star,
  Users, AlertTriangle as ViolationIcon, Settings2,
} from 'lucide-react';
import { useFeatureTier } from '../../contexts/FeatureTierContext';
// Section 2.4: user-saved Widget Library removed — no WidgetLibraryService import.
import {
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TemplateBuilderForm, FormField, LayoutSection, ContactsModuleConfig, ViolationsModuleConfig } from '../../types/templateTypes';
import { FIELD_TYPE_LABELS } from '../../types/templateTypes';
import type { PanelSelection } from '../form-builder/PropertiesPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormBlockExpansion = 'summary' | 'expanded';

export interface TemplateCanvasFormBlockProps {
  form: TemplateBuilderForm;
  order: number;
  optional: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  isDropHover?: boolean;
  selection: PanelSelection;
  onSelectForm: () => void;
  onToggleExpansion: () => void;
  onSelectSection: (sectionId: string) => void;
  onSelectField: (fieldId: string) => void;
  onRemoveForm: () => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onUpdateSection: (sectionId: string, updates: Partial<LayoutSection>) => void;
  onAddSection: () => void;
  onUpdateForm?: (updates: Partial<TemplateBuilderForm>) => void;
  onReorderSections: (formId: string, newSections: LayoutSection[]) => void;
  onSaveToLibrary?: () => void;
  onUpdateLibraryForm?: () => void;
  onRemoveFromLibrary?: () => void;
  ruleCount: number;
  internalRuleCount?: number;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  pendingDropSectionId?: string | null;
  pendingDropFieldType?: string | null;
  pendingDropInsertIndex?: number | null;
  activeFieldDropSectionId?: string | null;
  activeFieldDropInsertIndex?: number | null;
  showFieldDropTargets?: boolean;
  onCommitDrop?: (result: { label: string; required: boolean }) => void;
  onCancelDrop?: () => void;
  isDraggingSection?: boolean;
  onRemoveSection?: (sectionId: string) => void;
  onDeleteField?: (fieldId: string) => void;
  onDuplicateField?: (fieldId: string, sectionId: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fieldCompletionScore(field: FormField): boolean {
  return !!field.label && field.label.trim().length > 0;
}

function sectionCompletionScore(section: LayoutSection, fields: FormField[]): { done: number; total: number } {
  const sectionFields = fields.filter((f) => section.fieldIds.includes(f.id));
  if (sectionFields.length === 0) return { done: 0, total: 0 };
  const done = sectionFields.filter(fieldCompletionScore).length;
  return { done, total: sectionFields.length };
}

// ---------------------------------------------------------------------------
// Section 2.4: SaveWidgetModal + user-saved Widget Library removed entirely.
// Admins now configure data-bound fields via the pre-built registry
// (DATA_BOUND_WIDGET_REGISTRY) exposed through the "Data Widgets" palette
// tab and the PropertiesPanel's registry-backed custom data-bound picker.
// ---------------------------------------------------------------------------

/**
 * @component FieldRow
 *
 * Purpose:
 * - Render a draggable field row inside an expanded canvas section.
 *
 * Props:
 * - field: Form field contract displayed and dragged by the row.
 * - isSelected: Whether the row is selected in the canvas properties panel.
 * - currentFormId/currentSectionId: Local canvas location metadata for drag payloads.
 * - onClick/onDelete/onDuplicate/onMoveTo: Parent-owned local-first mutation callbacks.
 *
 * Behavior:
 * - Registers the field as a sortable canvas item with source metadata for cross-section and cross-form moves.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not call APIs, Dexie, or sync services directly.
 *
 * Performance:
 * - Uses dnd-kit transforms for drag feedback without owning persistence state.
 */
function FieldRow({ field, isSelected, onClick, onDelete, onDuplicate, otherSections, currentFormId, currentSectionId, onMoveTo }: {
  field: FormField;
  isSelected: boolean;
  onClick: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  otherSections?: LayoutSection[];
  currentFormId: string;
  currentSectionId?: string;
  onMoveTo?: (toSectionId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    data: {
      source: 'canvasField',
      fieldId: field.id,
      formId: currentFormId,
      sectionId: currentSectionId,
    },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const complete = fieldCompletionScore(field);
  // Section 2.4: Save-widget affordance removed.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const moveRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMoveMenu) return;
    const handler = (e: MouseEvent) => {
      if (moveRef.current && !moveRef.current.contains(e.target as Node)) setShowMoveMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMoveMenu]);

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        data-field-id={field.id}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={`group flex items-center gap-1.5 px-3 py-1.5 text-xs cursor-pointer transition-colors rounded mx-1 ${
          isSelected
            ? 'bg-blue-50 text-blue-800 ring-1 ring-blue-200'
            : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <div {...listeners} {...attributes} onClick={(e) => e.stopPropagation()} className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0 -ml-1">
          <GripVertical size={11} />
        </div>
        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${complete ? 'bg-green-400' : 'bg-amber-400'}`} />
        <span className="flex-1 truncate">{field.label || <span className="italic text-gray-400">Untitled field</span>}</span>
        <span className="text-[10px] text-gray-400 shrink-0">{FIELD_TYPE_LABELS[field.type]}</span>
        {field.required && <span className="text-[10px] text-red-500 shrink-0">*</span>}

        {/* Actions — visible on hover */}
        <div className="invisible group-hover:visible flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          {/* Section 2.4: Save-to-Widget-Library button removed. */}

          {/* Duplicate field */}
          {onDuplicate && (
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              title="Duplicate field"
              aria-label={`Duplicate ${field.label || 'field'}`}
              className="p-0.5 text-gray-300 hover:text-blue-500 transition-colors"
            >
              <Copy size={10} />
            </button>
          )}

          {/* Move to section */}
          {otherSections && otherSections.length > 0 && onMoveTo && (
            <div ref={moveRef} className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowMoveMenu((v) => !v); }}
                title="Move to section"
                className="p-0.5 text-gray-300 hover:text-blue-500 transition-colors"
              >
                <LayoutDashboard size={10} />
              </button>
              {showMoveMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                  <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Move to section</div>
                  {otherSections.map((s) => (
                    <button
                      key={s.id}
                      onClick={(e) => { e.stopPropagation(); onMoveTo(s.id); setShowMoveMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {s.title || `Section ${s.order + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delete */}
          {onDelete && (
            confirmDelete ? (
              <div className="flex items-center gap-0.5">
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(); setConfirmDelete(false); }}
                  title="Confirm delete"
                  className="p-0.5 rounded-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Check size={10} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                  title="Cancel"
                  className="p-0.5 rounded-sm text-gray-400 hover:bg-gray-100 transition-colors"
                >
                  <X size={10} />
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
                title="Delete field"
                className="p-0.5 text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 size={10} />
              </button>
            )
          )}
        </div>
      </div>
      {/* Section 2.4: SaveWidgetModal removed. */}
    </>
  );
}

// ---------------------------------------------------------------------------
// FieldDropWizard — 2-step mini-wizard shown after a palette drop
// Step 1: label entry  →  Step 2: required toggle + confirm
// ---------------------------------------------------------------------------

const FIELD_LABEL_SUGGESTIONS: Record<string, string[]> = {
  toggle: ['Violations Found', 'Access Road Clear', 'Suppression System Present', 'Inspector Verified'],
  text: ['Inspector Name', 'Property Address', 'Permit Number', 'Owner Name'],
  textarea: ['Inspector Notes', 'Violation Description', 'Corrective Action Required', 'Observations'],
  number: ['Unit Count', 'Occupant Load', 'Floor Level', 'Distance (ft)'],
  measurement: ['Clearance Distance (ft)', 'Vegetation Height (in)', 'Hose Length (ft)', 'Pressure (PSI)'],
  dropdown: ['Occupancy Type', 'Inspection Result', 'Risk Level', 'Condition Rating'],
  radioGroup: ['Pass / Fail', 'Compliance Status', 'Severity Level', 'Inspector Decision'],
  checkboxGroup: ['Deficiencies Found', 'Equipment Checked', 'Documents Reviewed'],
  date: ['Inspection Date', 'Last Service Date', 'Next Due Date', 'Issue Date'],
  camera: ['Violation Photo', 'Property Photo', 'Equipment Photo'],
  photo: ['Site Photo', 'Damage Photo', 'Before / After'],
};

function FieldDropWizard({ fieldType, onCommit, onCancel }: {
  fieldType: string;
  onCommit: (result: { label: string; required: boolean }) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasAI } = useFeatureTier();
  useEffect(() => { inputRef.current?.focus(); }, []);

  const aiSuggestions = hasAI ? (FIELD_LABEL_SUGGESTIONS[fieldType] || []).slice(0, 3) : [];

  if (step === 1) {
    return (
      <div className="mx-3 my-1 px-2.5 py-2 rounded-lg bg-blue-50 ring-1 ring-blue-300 flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-blue-600 font-medium uppercase tracking-wide">New {fieldType}</span>
        </div>
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Field label…"
            className="flex-1 min-w-0 bg-white border border-blue-200 rounded-sm px-2 py-1 text-xs text-gray-800 placeholder-gray-400 outline-hidden focus:ring-1 focus:ring-blue-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); if (label.trim()) setStep(2); }
              if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
            }}
          />
          <button onClick={() => { if (label.trim()) setStep(2); }} className="p-1 text-blue-500 hover:text-blue-700 shrink-0" title="Next"><Check size={11} /></button>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 shrink-0" title="Cancel"><X size={11} /></button>
        </div>
        {aiSuggestions.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <Sparkles size={9} className="text-purple-400 shrink-0" />
            {aiSuggestions.map((s: string) => (
              <button
                key={s}
                onClick={() => { setLabel(s); inputRef.current?.focus(); }}
                className="px-1.5 py-0.5 text-[9px] bg-purple-50 border border-purple-200 text-purple-600 rounded-sm hover:bg-purple-100 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-3 my-1 px-2.5 py-2 rounded-lg bg-blue-50 ring-1 ring-blue-300 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
      <div className="text-[10px] text-blue-600 font-medium truncate">{label || '(untitled)'}</div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="rounded-sm border-gray-300" />
        <span className="text-xs text-gray-700">Required</span>
      </label>
      <div className="flex items-center justify-end gap-1">
        <button onClick={() => setStep(1)} className="px-2 py-0.5 text-[10px] text-gray-500 border border-gray-200 rounded-sm hover:bg-gray-50">Back</button>
        <button onClick={() => onCommit({ label, required })} className="px-2 py-0.5 text-[10px] font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700">Add field</button>
      </div>
    </div>
  );
}
/**
 * @component SectionBlock
 *
 * Purpose:
 * - Render a draggable canvas section and its sortable field rows.
 *
 * Props:
 * - formId: Owning form identifier used for drag metadata.
 * - section/fields/allSections: Local form layout data used to render rows and insert markers.
 * - activeFieldDropSectionId/activeFieldDropInsertIndex: Parent-resolved visual drop target.
 * - onMoveField/onUpdateSection/onRemoveSection: Parent-owned local-first mutation callbacks.
 *
 * Behavior:
 * - Displays field insert markers while palette items or existing canvas fields are dragged.
 * - Registers sections and fields with the shared canvas drag context.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist directly; all mutations route through callbacks.
 *
 * Performance:
 * - Keeps drag visuals local to the section and avoids additional subscriptions.
 */
function SectionBlock({ formId, section, fields, allSections, selectedFieldId, selectedSectionId, onSelectSection, onSelectField, onUpdateSection, onMoveField, onRemoveSection, onDeleteField, onDuplicateField, isPendingDrop, pendingDropFieldType, pendingDropInsertIndex, activeFieldDropSectionId, activeFieldDropInsertIndex, showFieldDropTargets, onCommitDrop, onCancelDrop }: {
  formId: string;
  section: LayoutSection;
  fields: FormField[];
  allSections: LayoutSection[];
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  onSelectSection: (id: string) => void;
  onSelectField: (id: string) => void;
  onUpdateSection: (sectionId: string, updates: Partial<LayoutSection>) => void;
  onMoveField: (fieldId: string, fromSectionId: string, toSectionId: string, newIndex: number) => void;
  onRemoveSection: (sectionId: string) => void;
  onDeleteField?: (fieldId: string) => void;
  onDuplicateField?: (fieldId: string, sectionId: string) => void;
  isPendingDrop?: boolean;
  pendingDropFieldType?: string | null;
  pendingDropInsertIndex?: number | null;
  activeFieldDropSectionId?: string | null;
  activeFieldDropInsertIndex?: number | null;
  showFieldDropTargets?: boolean;
  onCommitDrop?: (result: { label: string; required: boolean }) => void;
  onCancelDrop?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: {
      source: 'canvasSection',
      formId,
      sectionId: section.id,
    },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const sectionFields = fields.filter((f) => section.fieldIds.includes(f.id));
  const { done, total } = sectionCompletionScore(section, fields);
  const isSectionSelected = selectedSectionId === section.id;
  const [confirmRemove, setConfirmRemove] = useState(false);
  const canRemove = section.fieldIds.length === 0;
  const isSectionDropHover = activeFieldDropSectionId === section.id;
  const activeInsertIndex = isSectionDropHover
    ? (activeFieldDropInsertIndex ?? null)
    : !!isPendingDrop
      ? (pendingDropInsertIndex ?? null)
      : null;

  const shouldRenderActiveSlot = activeInsertIndex != null && (showFieldDropTargets || !!isPendingDrop);
  /**
   * Render the field-insert affordance at a given list index.
   *
   * Two affordances share this position:
   * - Drag-time `FieldInsertSlot` marker ("Add here") while the user is dragging
   *   a palette field/widget or an existing canvas field.
   * - Post-drop `FieldDropWizard` label/required prompt for palette field types.
   *
   * They MUST never render simultaneously at the same index. Previously the
   * wizard was rendered once at the end of the section, decoupled from the
   * marker, which caused the marker to remain visible at `pendingDropInsertIndex`
   * while the wizard appeared at the bottom — making it look like the drop
   * landed in the wrong place. Rendering both inline at the resolved insert
   * index keeps visual position and committed insert index in lockstep.
   */
  const renderActiveFieldInsertSlot = (index: number) => {
    if (
      isPendingDrop
      && onCommitDrop
      && onCancelDrop
      && (pendingDropInsertIndex ?? null) === index
    ) {
      return (
        <FieldDropWizard
          fieldType={pendingDropFieldType || 'field'}
          onCommit={onCommitDrop}
          onCancel={onCancelDrop}
        />
      );
    }
    if (shouldRenderActiveSlot && activeInsertIndex === index) {
      return <FieldInsertSlot sectionId={section.id} insertIndex={index} isActive />;
    }
    return null;
  };

  return (
    <div ref={setNodeRef} style={style} data-section-id={section.id} className="mb-1">
      {/* Section header */}
      <div
        onClick={(e) => { e.stopPropagation(); onSelectSection(section.id); }}
        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors rounded mx-1 ${
          isSectionSelected
            ? 'bg-purple-50 text-purple-800 ring-1 ring-purple-200'
            : 'text-gray-500 hover:bg-gray-50'
        }`}
      >
        <div {...listeners} {...attributes} onClick={(e) => e.stopPropagation()} className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0 -ml-1">
          <GripVertical size={11} />
        </div>
        <LayoutDashboard size={11} className="shrink-0 text-gray-400" />
        <span className="flex-1 truncate">{section.title || `Section ${section.order + 1}`}</span>
        {total > 0 && (
          <span className={`text-[10px] shrink-0 ${done === total ? 'text-green-500' : 'text-amber-500'}`}>
            {done}/{total}
          </span>
        )}
        {/* Section remove button */}
        <div className="shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
          {confirmRemove ? (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-red-500 font-medium">Remove?</span>
              <button
                onClick={() => { onRemoveSection(section.id); setConfirmRemove(false); }}
                title="Confirm remove"
                className="p-0.5 rounded-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <Check size={11} />
              </button>
              <button
                onClick={() => setConfirmRemove(false)}
                title="Cancel"
                className="p-0.5 rounded-sm text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <X size={11} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => canRemove ? setConfirmRemove(true) : undefined}
              title={canRemove ? 'Remove section' : 'Remove all fields first'}
              className={`p-1 rounded transition-colors ${
                canRemove
                  ? 'text-gray-300 hover:text-red-400 hover:bg-red-50'
                  : 'text-gray-200 cursor-not-allowed'
              }`}
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>
        <div className="flex items-center bg-gray-100 rounded-sm p-0.5 gap-px shrink-0" onClick={(e) => e.stopPropagation()}>
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              onClick={() => onUpdateSection(section.id, { columns: n })}
              className={`w-5 h-5 rounded text-[10px] font-medium transition-colors ${
                (section.columns || 1) === n ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-400 hover:text-gray-600'
              }`}
              title={`${n} column${n > 1 ? 's' : ''}`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      {/* Fields */}
      <SectionFieldDropZone sectionId={section.id} isActiveDropTarget={isSectionDropHover || !!isPendingDrop}>
      <div className="ml-3 border-l border-gray-100 pl-1 mt-0.5">
        {sectionFields.length === 0 ? (
          // Empty-section render path. When a palette drop is awaiting a label,
          // `renderActiveFieldInsertSlot(0)` returns the FieldDropWizard inline,
          // which replaces the "No fields" hint. While dragging, it returns the
          // active marker. When idle it returns null and the hint remains.
          <>
            {renderActiveFieldInsertSlot(0)}
            {!isPendingDrop && (
              <div className="px-3 py-1.5 text-[10px] text-gray-400 italic">No fields — drag here to add</div>
            )}
          </>
        ) : (
          <SortableContext items={sectionFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            {renderActiveFieldInsertSlot(0)}
            {sectionFields.map((field, idx) => (
              <Fragment key={field.id}>
                <FieldRow
                  field={field}
                  isSelected={selectedFieldId === field.id}
                  onClick={() => onSelectField(field.id)}
                  onDelete={onDeleteField ? () => onDeleteField(field.id) : undefined}
                  onDuplicate={onDuplicateField ? () => onDuplicateField(field.id, section.id) : undefined}
                  currentFormId={formId}
                  currentSectionId={section.id}
                  otherSections={allSections.filter((s) => s.id !== section.id)}
                  onMoveTo={(toSectionId) => onMoveField(field.id, section.id, toSectionId, allSections.find((s) => s.id === toSectionId)?.fieldIds.length ?? 0)}
                />
                {renderActiveFieldInsertSlot(idx + 1)}
              </Fragment>
            ))}
          </SortableContext>
        )}
        {/*
         * The post-drop FieldDropWizard is rendered inline by
         * renderActiveFieldInsertSlot at the resolved pendingDropInsertIndex.
         * It MUST NOT also be rendered here (or anywhere outside the field
         * list) — doing so caused the wizard to appear at the bottom of the
         * section while the marker stayed at the hover index, creating the
         * misleading impression that the drop landed in the wrong place.
         */}
      </div>
      </SectionFieldDropZone>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionFieldDropZone — droppable for palette fields
// ---------------------------------------------------------------------------

function SectionFieldDropZone({ sectionId, isActiveDropTarget, children }: { sectionId: string; isActiveDropTarget?: boolean; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `field-dropzone-${sectionId}` });
  return (
    <div ref={setNodeRef} className={`rounded-sm transition-colors ${isOver || isActiveDropTarget ? 'bg-blue-50/80 ring-1 ring-blue-300 ring-inset' : ''}`}>
      {children}
    </div>
  );
}

/**
 * @component FieldInsertSlot
 *
 * Purpose:
 * - Render and register an exact field insertion target inside a canvas section.
 *
 * Props:
 * - sectionId: Section identifier encoded into the dnd-kit droppable id.
 * - insertIndex: Destination fieldIds index encoded into the dnd-kit droppable id.
 * - isActive: Whether the marker is currently visible as the resolved insertion point.
 *
 * Behavior:
 * - Registers `field-insert-slot-{sectionId}-{insertIndex}` so drag end can commit to the same index shown by the marker.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist directly; parent drop handlers own all local-first updates.
 *
 * Performance:
 * - Uses a single dnd-kit droppable ref and otherwise remains presentation-only.
 */
function FieldInsertSlot({ sectionId, insertIndex, isActive }: { sectionId: string; insertIndex: number; isActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `field-insert-slot-${sectionId}-${insertIndex}` });
  const active = isActive || isOver;

  return (
    <div
      ref={setNodeRef}
      className={`mx-2 my-1 rounded-lg border-2 border-dashed transition-all ${active ? 'border-blue-400 bg-blue-50 py-3' : 'border-transparent bg-transparent py-1'}`}
    >
      <div className={`text-[10px] font-medium text-center transition-opacity ${active ? 'text-blue-600 opacity-100' : 'opacity-0'}`}>
        Add here
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContextMenu — three-dot menu for form block actions
// ---------------------------------------------------------------------------

function FormContextMenu({
  onRemove,
  onUpdateLibraryForm,
  onRemoveFromLibrary,
  isHeader,
  canRemove,
}: {
  onRemove: () => void;
  onUpdateLibraryForm?: () => void;
  onRemoveFromLibrary?: () => void;
  isHeader: boolean;
  canRemove: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 220 });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) { setConfirmRemove(false); return; }
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setConfirmRemove(false);
      }
    };
    const onScroll = () => { setOpen(false); setConfirmRemove(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const menu = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Section 2.3: "Edit form" and "Edit content" menu items removed — forms are edited inline on the canvas; the header pencil toggles inline expansion. */}
      {onUpdateLibraryForm && (
        <>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => { setOpen(false); onUpdateLibraryForm(); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <BookMarked size={12} className="text-amber-500" /> Update library form
          </button>
        </>
      )}
      {onRemoveFromLibrary && (
        <button
          onClick={() => { setOpen(false); onRemoveFromLibrary(); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <Star size={12} /> Remove from library
        </button>
      )}
      <div className="my-1 border-t border-gray-100" />
      {!canRemove ? (
        <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 cursor-not-allowed">
          <Trash2 size={12} className="shrink-0" />
          <span>Remove all sections &amp; fields first</span>
        </div>
      ) : confirmRemove ? (
        <div className="px-3 py-1.5">
          <p className="text-[10px] text-red-600 font-medium mb-1.5">Remove this form from the template?</p>
          <div className="flex gap-1.5">
            <button
              onClick={() => { setOpen(false); setConfirmRemove(false); onRemove(); }}
              className="flex-1 py-1 text-[10px] font-medium text-white bg-red-500 hover:bg-red-600 rounded-sm transition-colors"
            >
              Yes, remove
            </button>
            <button
              onClick={() => setConfirmRemove(false)}
              className="flex-1 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setConfirmRemove(true)}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={12} /> Remove from template
        </button>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        title="More actions"
        className="p-1 text-gray-400 hover:text-gray-600 rounded-sm transition-colors"
      >
        <MoreVertical size={13} />
      </button>
      {menu}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExpandedSectionDropZone — droppable wrapper for the expanded section list
// ---------------------------------------------------------------------------

function ExpandedSectionDropZone({ formId, children }: { formId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `section-dropzone-${formId}` });
  return (
    <div
      ref={setNodeRef}
      className={`transition-colors rounded-b-xl ${isOver ? 'bg-purple-50 ring-2 ring-purple-300 ring-inset' : ''}`}
    >
      {children}
    </div>
  );
}

function SectionInsertSlot({ formId, index }: { formId: string; index: number }) {
  const id = `section-insert-slot-${formId}-${index}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`mx-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-1.5 text-[10px] font-medium ${
        isOver
          ? 'border-(--brand-primary) bg-orange-50 text-orange-600 py-3'
          : 'border-orange-300 bg-orange-50/30 text-orange-400 py-1.5'
      }`}
    >
      <Plus size={10} className={isOver ? 'text-orange-500' : 'text-orange-300'} />
      {isOver ? 'Drop section here' : 'Drop here'}
    </div>
  );
}


// ---------------------------------------------------------------------------
// TemplateCanvasFormBlock
// ---------------------------------------------------------------------------

/**
 * @component TemplateCanvasFormBlock
 *
 * Purpose:
 * - Render one form card, its expanded section tree, and field-level canvas controls.
 *
 * Props:
 * - form/order/optional: Local form state and template ordering metadata.
 * - selection/isExpanded/isDropHover: Parent-owned canvas selection and drag visual state.
 * - onUpdateForm/onReorderSections: Host-provided local-first mutation callbacks.
 * - pendingDrop and activeFieldDrop props: Drag/drop prompt and marker state from TemplateCanvas.
 *
 * Behavior:
 * - Composes form headers, section sortables, field sortables, and drop markers.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not call API clients, Dexie, or sync services directly.
 *
 * Performance:
 * - Uses dnd-kit sortable transforms and parent-provided memoized callbacks where available.
 */
/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - User actions route through callbacks that host apps connect to Dexie-backed services.
 *
 * Sync Strategy:
 * - Background sync remains outside this UI component.
 *
 * Conflict Resolution:
 * - Host local-first services own conflict resolution for form/layout updates.
 */
export function TemplateCanvasFormBlock({
  form,
  order,
  optional,
  isSelected,
  isExpanded,
  isDropHover,
  selection,
  onSelectForm,
  onToggleExpansion,
  onSelectSection,
  onSelectField,
  onRemoveForm,
  onUpdateSection,
  onAddSection,
  onUpdateForm,
  onReorderSections,
  onSaveToLibrary,
  onUpdateLibraryForm,
  onRemoveFromLibrary,
  ruleCount,
  internalRuleCount,
  dragHandleProps,
  pendingDropSectionId,
  pendingDropFieldType,
  pendingDropInsertIndex,
  activeFieldDropSectionId,
  activeFieldDropInsertIndex,
  showFieldDropTargets,
  onCommitDrop,
  onCancelDrop,
  isDraggingSection,
  onRemoveSection,
  onDeleteField,
  onDuplicateField,
}: TemplateCanvasFormBlockProps) {
  const expansion: FormBlockExpansion = isExpanded ? 'expanded' : 'summary';

  const isModule = form.formKind === 'module';
  const isHeader = form.formKind === 'header';
  const isLibrarySource = !!form.reusable && !form.librarySourceId;
  const isCleanCopy = !!form.librarySourceId && !form.libraryDiverged;
  const isDivergedCopy = !!form.librarySourceId && !!form.libraryDiverged;
  const showFilledStar = isLibrarySource || isCleanCopy;

  const sections = [...(form.layout?.sections ?? [])].sort((a, b) => a.order - b.order);

  const handleRemoveSectionLocal = useCallback((sectionId: string) => {
    if (onRemoveSection) {
      onRemoveSection(sectionId);
    } else {
      const updated = (form.layout?.sections ?? []).filter((s) => s.id !== sectionId).map((s, i) => ({ ...s, order: i }));
      onReorderSections(form._id, updated);
    }
  }, [onRemoveSection, form.layout?.sections, form._id, onReorderSections]);

  const handleMoveField = useCallback((fieldId: string, fromSectionId: string, toSectionId: string, newIndex: number) => {
    const updatedSections = (form.layout?.sections ?? []).map((s) => {
      if (s.id === fromSectionId && fromSectionId !== toSectionId) {
        return { ...s, fieldIds: s.fieldIds.filter((id) => id !== fieldId) };
      }
      if (s.id === toSectionId) {
        const filtered = s.fieldIds.filter((id) => id !== fieldId);
        filtered.splice(newIndex, 0, fieldId);
        return { ...s, fieldIds: filtered };
      }
      return s;
    }).map((s, i) => ({ ...s, order: i }));
    onReorderSections(form._id, updatedSections);
  }, [form.layout?.sections, form._id, onReorderSections]);

  const totalFields = (form.fields ?? []).length;
  const completedFields = (form.fields ?? []).filter(fieldCompletionScore).length;
  const overallComplete = totalFields > 0 && completedFields === totalFields;

  const selectedFieldId = selection?.level === 'field' ? selection.fieldId : null;
  const selectedSectionId = selection?.level === 'section' ? selection.sectionId : null;

  const handleToggleExpansion = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpansion();
  }, [onToggleExpansion]);

  // Header forms use amber, standard forms use their display color
  const formColor = isHeader ? '#d97706' : (form.display?.color || '#6366f1');

  const borderCls = isSelected
    ? (isHeader
        ? 'border-amber-400 shadow-md ring-2 ring-amber-200'
        : 'border-(--brand-primary) shadow-md ring-2 ring-(--brand-primary)/20')
    : isDropHover
      ? 'border-blue-300 shadow-md ring-2 ring-blue-200'
    : (isHeader
        ? 'border-amber-200 shadow-xs hover:shadow-md hover:border-amber-300'
        : 'border-gray-200 shadow-xs hover:shadow-md');

  const handleCardClick = useCallback(() => {
    onSelectForm();
    // onSelectForm already adds this form to expandedForms via handleSelectForm in TemplateCanvas.
    // For non-header forms, leave expansion toggling to the chevron button.
    // No additional toggle needed here.
  }, [onSelectForm]);

  // ── Module card — locked, code-rendered ──
  if (isModule) {
    const mc = form.moduleConfig;
    const isContacts = mc?.moduleType === 'contacts';
    const isViolations = mc?.moduleType === 'violations';
    const moduleColor = '#ea580c'; // all system modules use orange — same as violations/instructions
    const ModuleIcon = isContacts ? Users : ViolationIcon;
    const moduleBadgeClass = 'bg-orange-100 text-orange-700';
    const borderModule = isSelected
      ? 'border-2 shadow-md border-orange-400 ring-2 ring-orange-200'
      : 'border border-orange-200 hover:border-orange-300 shadow-xs hover:shadow-md';

    return (
      <div
        className={`rounded-xl bg-white overflow-hidden transition-shadow ${borderModule}`}
        onClick={() => { onSelectForm(); }}
      >
        <div className="h-1 w-full" style={{ backgroundColor: moduleColor }} />
        <div className="flex items-center gap-2 px-3 py-2.5">
          <div {...dragHandleProps} className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0" onClick={(e) => e.stopPropagation()}>
            <GripVertical size={14} />
          </div>
          <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: moduleColor + '22' }}>
            <ModuleIcon size={13} style={{ color: moduleColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-gray-900 truncate">{form.name}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${moduleBadgeClass}`}>
                {isContacts ? 'Contacts' : 'Violations'} Module
              </span>
              <span className="text-[9px] px-1 py-0.5 rounded-sm bg-gray-100 text-gray-400 shrink-0 flex items-center gap-0.5">
                <Lock size={8} /> system
              </span>
            </div>
            {isContacts && mc && !isExpanded && (
              <div className="text-[10px] text-gray-400 mt-0.5">
                {(mc as ContactsModuleConfig).roles.filter(r => r.role).map(r =>
                  `${r.role}${r.minRequired > 0 ? ` (min ${r.minRequired})` : ''}`
                ).join(' · ')}
              </div>
            )}
            {isViolations && mc && !isExpanded && (
              <div className="text-[10px] text-gray-400 mt-0.5">
                {(mc as ViolationsModuleConfig).codeSetIds.length} code set{(mc as ViolationsModuleConfig).codeSetIds.length !== 1 ? 's' : ''} ·
                {(mc as ViolationsModuleConfig).allowAiSearch ? ' AI search' : ' keyword search'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onRemoveForm}
              title="Remove module"
              className="p-1 rounded-sm text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={13} />
            </button>
            <button
              onClick={handleToggleExpansion}
              title={isExpanded ? 'Collapse configuration' : 'Expand configuration'}
              className="p-1 rounded-sm text-gray-300 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>
        </div>
        {/* Inline module configurator */}
        {isExpanded && (
          <div
            className="border-t border-orange-100 bg-orange-50/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 pt-2 pb-1 flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 select-none">Module Configuration</span>
              <span className="text-[10px] text-gray-300">— changes save automatically</span>
            </div>
            {isContacts && mc && (
              <ContactsConfigurator
                config={mc as ContactsModuleConfig}
                onChange={(cfg) => onUpdateForm?.({ moduleConfig: cfg })}
              />
            )}
            {isViolations && mc && (
              <ViolationsConfigurator
                config={mc as ViolationsModuleConfig}
                onChange={(cfg) => onUpdateForm?.({ moduleConfig: cfg })}
              />
            )}
            {!mc && (
              <div className="px-4 py-4 text-xs text-gray-400 text-center italic">No module configuration found.</div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden transition-shadow ${borderCls}`}
      onClick={handleCardClick}
    >
      {/* Color bar — amber for header, brand color for standard */}
      <div className="h-1 w-full" style={{ backgroundColor: formColor }} />

      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Drag handle — hidden for header (pinned to top, no reorder) */}
        {isHeader ? (
          <div className="text-amber-300 shrink-0" title="Pinned to top — cannot be reordered">
            <Lock size={13} />
          </div>
        ) : (
          <div {...dragHandleProps} className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0" onClick={(e) => e.stopPropagation()}>
            <GripVertical size={14} />
          </div>
        )}

        {/* Order badge / header icon */}
        {isHeader ? (
          <div className="w-5 h-5 rounded-full flex items-center justify-center bg-amber-100 shrink-0">
            <FileText size={10} className="text-amber-600" />
          </div>
        ) : (
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
            style={{ backgroundColor: formColor }}
          >
            {order}
          </div>
        )}

        {/* Form name + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">{form.name}</span>
            {isHeader && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium shrink-0">Header</span>
            )}
            {optional && !isHeader && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500 shrink-0">optional</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {isCleanCopy && (
              <span className="text-[10px] flex items-center gap-0.5 text-violet-600 shrink-0">
                <Link2 size={9} /> Library: {form.librarySourceName}
              </span>
            )}
            {isDivergedCopy && (
              <span className="text-[10px] flex items-center gap-0.5 text-gray-400 shrink-0" title={`Started from library form "${form.librarySourceName}" — independently edited`}>
                <Link2 size={9} className="opacity-40" /> Based on: {form.librarySourceName}
              </span>
            )}
            {!isHeader && totalFields > 0 && (
              <span className={`text-[10px] flex items-center gap-0.5 ${overallComplete ? 'text-green-600' : 'text-amber-600'}`}>
                {overallComplete ? <Check size={9} /> : <AlertCircle size={9} />}
                {completedFields}/{totalFields} fields
              </span>
            )}
            {!isHeader && (
              <span className="text-[10px] text-gray-400">{sections.length} section{sections.length !== 1 ? 's' : ''}</span>
            )}
            {ruleCount > 0 && (
              <span className="text-[10px] text-blue-500">{ruleCount} rule{ruleCount !== 1 ? 's' : ''}</span>
            )}
            {(internalRuleCount ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5"
                title={`${internalRuleCount} same-form rule${(internalRuleCount ?? 0) !== 1 ? 's' : ''}`}
              >
                <span aria-hidden>↻</span>
                {internalRuleCount} internal
              </span>
            )}
            {isHeader && (
              <span className="text-[10px] text-gray-400">Rich text · Always shown first</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          {!isHeader && (
            <button
              onClick={onSaveToLibrary}
              title={
                showFilledStar
                  ? (isLibrarySource ? 'This IS your library form — click to remove from library' : 'Clean library copy — click to disassociate')
                  : (isDivergedCopy ? 'Independently edited — no longer linked to library' : 'Add to Form Library')
              }
              disabled={isDivergedCopy}
              className={`p-1 rounded transition-colors ${
                showFilledStar
                  ? 'text-amber-400 hover:text-amber-600'
                  : isDivergedCopy
                    ? 'text-gray-200 cursor-default'
                    : 'text-gray-300 hover:text-amber-400'
              }`}
            >
              <Star size={13} fill={showFilledStar ? 'currentColor' : 'none'} />
            </button>
          )}
          {/* Section 2.3: non-header pencil removed — field editing happens inline on the canvas. */}
          {isHeader && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpansion(); }}
              title={isExpanded ? 'Collapse editor' : 'Edit header content'}
              className="p-1 text-gray-400 hover:text-amber-600 rounded-sm transition-colors"
            >
              <Pencil size={13} />
            </button>
          )}
          <FormContextMenu
            onRemove={onRemoveForm}
            onUpdateLibraryForm={!isHeader && isLibrarySource ? onUpdateLibraryForm : undefined}
            onRemoveFromLibrary={!isHeader && isLibrarySource ? onRemoveFromLibrary : undefined}
            isHeader={isHeader}
            canRemove={(form.fields ?? []).length === 0 && sections.every((s) => s.fieldIds.length === 0)}
          />
          {!isHeader && (
            <button
              onClick={handleToggleExpansion}
              title={isExpanded ? 'Collapse' : 'Expand'}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-sm transition-colors"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
          {isHeader && (
            <button
              onClick={handleToggleExpansion}
              title={!isExpanded ? 'Show preview' : 'Hide preview'}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-sm transition-colors"
            >
              {!isExpanded
                ? <ChevronRight size={14} />
                : <ChevronDown size={14} />
              }
            </button>
          )}
        </div>
      </div>

      {/* Header form — rich text editor */}
      {isHeader && isExpanded && (
        <div onClick={(e) => e.stopPropagation()}>
          <HeaderRichEditor
            content={form.description || ''}
            onChange={(html) => onUpdateForm?.({ description: html })}
          />
        </div>
      )}

      {/* Summary view — field count chips per section (standard forms only) */}
      {!isHeader && expansion === 'summary' && sections.length > 0 && (
        <div className="px-3 pb-2.5 flex flex-wrap gap-1.5">
          {sections.map((section) => {
            const { done, total } = sectionCompletionScore(section, form.fields ?? []);
            return (
              <div
                key={section.id}
                onClick={(e) => { e.stopPropagation(); onSelectSection(section.id); }}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px] text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <span className="truncate max-w-[100px]">{section.title || `Section ${section.order + 1}`}</span>
                <span className={`font-medium ${done === total && total > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  {total}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Expanded view — full tree (standard forms only) */}
      {!isHeader && !isModule && expansion === 'expanded' && (
        <ExpandedSectionDropZone formId={form._id}>
          <div className="pb-2">
          {sections.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">
              No sections yet — <button onClick={(e) => { e.stopPropagation(); onAddSection(); }} className="text-(--brand-primary) hover:underline">add one</button>
            </div>
          ) : (
            <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {isDraggingSection && <SectionInsertSlot formId={form._id} index={0} />}
              {sections.map((section, idx) => (
                <Fragment key={section.id}>
                <SectionBlock
                    formId={form._id}
                    section={section}
                    fields={form.fields ?? []}
                    allSections={sections}
                    selectedFieldId={selectedFieldId}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={onSelectSection}
                    onSelectField={onSelectField}
                    onUpdateSection={onUpdateSection}
                    onMoveField={handleMoveField}
                    onRemoveSection={handleRemoveSectionLocal}
                    onDeleteField={onDeleteField}
                    onDuplicateField={onDuplicateField}
                    isPendingDrop={pendingDropSectionId === section.id}
                    pendingDropFieldType={pendingDropFieldType}
                    pendingDropInsertIndex={pendingDropSectionId === section.id ? pendingDropInsertIndex : null}
                    activeFieldDropSectionId={activeFieldDropSectionId}
                    activeFieldDropInsertIndex={activeFieldDropSectionId === section.id ? activeFieldDropInsertIndex : null}
                    showFieldDropTargets={showFieldDropTargets}
                    onCommitDrop={onCommitDrop}
                    onCancelDrop={onCancelDrop}
                  />
                  {isDraggingSection && <SectionInsertSlot formId={form._id} index={idx + 1} />}
                </Fragment>
              ))}
            </SortableContext>
          )}
          {/* Add section button */}
          <div className="px-3 pt-1">
            <button
              onClick={(e) => { e.stopPropagation(); onAddSection(); }}
              className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-gray-400 hover:text-gray-600 border border-dashed border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              <Plus size={11} /> Add Section
            </button>
          </div>
          </div>
        </ExpandedSectionDropZone>
      )}
    </div>
  );
}

export default TemplateCanvasFormBlock;
